import { OrderItem, OrderItemReports } from '../api/types';
import { OrderReport } from '../orders/types';
import { NEEDS_CONTACT_PROVIDER, USER_ROLES, USER_ROLES_AVAILABLE } from './constants';
import { IConfiguration, ISelectItem } from './types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);

const CENTRAL_TIME_ZONE = 'America/Chicago';

// Server error responses come in 3 shapes: a plain string body (e.g. `BadRequest("No payment
// method on file")`), a JSON `{ message }` body for any unhandled 500 since the 2026-07-19 global
// exception handler (see Program.cs), or an ASP.NET ModelState dictionary - `{ FieldOrCode:
// ["message", ...] }` - from `return BadRequest(ModelState)` (e.g. AuthController.Register's
// Identity validation errors: DuplicateEmail/DuplicateUserName/PasswordTooShort/etc., each with a
// human-readable Description). Centralized here so every saga that surfaces a server error inline
// extracts it the same way instead of only handling the string case.
export function extractErrorMessage(data: unknown, fallback: string): string {
    if (typeof data === 'string') return data;
    if (data && typeof data === 'object' && typeof (data as any).message === 'string') return (data as any).message;
    if (data && typeof data === 'object') {
        const messages = Object.values(data as Record<string, unknown>)
            .flat()
            .filter((v): v is string => typeof v === 'string');
        if (messages.length > 0) return messages.join(' ');
    }
    return fallback;
}

export async function loadConfig(environment?: string): Promise<IConfiguration> {
    const env: string = environment ?? (window as any).environment ?? 'local';
    const configFile: IConfiguration = await import(`../../config/config.${env.toLowerCase()}.json`);
    return configFile;
}

export const withPayloadType = <T>() => (t: T) => ({ payload: t });

export const base64EncodeFile = async (file: File) => {
    return new Promise((resolve, reject) => {
        try {
            const fileReader = new FileReader();
            fileReader.onload = (_: ProgressEvent<FileReader>) => {
                resolve(fileReader.result);
            }

            fileReader.readAsDataURL(file);

        }
        catch(err){
            reject(err);
        }
    });
};

export const formatUtc = (dateTime?: string | null | number) => dayjs(dateTime).isValid() ? dayjs(dateTime).utc(false).format("YYYY-MM-DD[T]HH:mm:ss") : dateTime?.toString() ?? undefined;
export const formatUtcTime = (dateTime?: string | null | number) => dayjs(dateTime).isValid() ? dayjs(dateTime).utc(false).format("HHmmss") : dateTime?.toString() ?? undefined;
// Aug 2026 - the app has no real per-org timezone data (see formatDateTime's own comment below) -
// every stored collection/order timestamp is meant to represent Central Time specifically (the
// lab's own timezone), not the browser's local one. formatUtc(Date.now()) converts "now" to true
// UTC, but every display elsewhere labels the stored value "CT" - during CDT (UTC-5) that read 5
// hours ahead of actual Central time for any browser not itself in Central. These convert the
// current real instant to Central's actual wall-clock time instead (correctly handling CST/CDT),
// agnostic of the viewer's own timezone - use these instead of formatUtc(Date.now())/
// formatUtcTime(Date.now()) for any "right now" timestamp meant to display as Central Time.
export const formatCentralTimeNow = () => dayjs().tz(CENTRAL_TIME_ZONE).format("YYYY-MM-DD[T]HH:mm:ss");
export const formatCentralTimeOfDayNow = () => dayjs().tz(CENTRAL_TIME_ZONE).format("HHmmss");
// Aug 2026 - same root cause as above, different shape: EditOrder.tsx re-displays an EXISTING
// stored collectionDatetime (a naive string already written in Central Time by the server) in
// the edit form's date/time picker. dayjs parses a naive string as the browser's own local time
// by default, so formatUtc(existingCentralString) was reinterpreting an already-Central value
// through the viewer's own zone and shifting it again - wrong in the opposite direction from the
// Date.now() bug above, but the same underlying issue (no real per-org timezone data, browser
// zone leaking into a value that's always meant to be Central). dayjs.tz(str, zone) parses the
// naive string AS that zone directly instead of converting into it, so this just re-formats the
// existing Central value without shifting it at all.
export const formatCentralTimeString = (dateTime?: string | null) =>
    dateTime && dayjs.tz(dateTime, CENTRAL_TIME_ZONE).isValid()
        ? dayjs.tz(dateTime, CENTRAL_TIME_ZONE).format("YYYY-MM-DD[T]HH:mm:ss")
        : dateTime ?? undefined;
export const formatDOB = (dob?: string | null) => dayjs(dob).isValid() ? dayjs(dob).format("MM/DD/YYYY") : dob;
// Every stored order/collection timestamp is written server-side with DateTime.Now (the server
// machine's own OS clock, not the clinic's) into a naive, offset-less string - there's no real
// per-organization timezone data anywhere in the system to convert from/to, so this can only ever
// label the server's own zone (Central), not truly localize to the viewer's or clinic's own time.
export const formatDateTime = (dateTime?: string | null | number) => dayjs(dateTime).isValid() ? `${dayjs(dateTime).format("MM/DD/YYYY h:mm A")} CT` : '';
export const formatMessageDate = () => dayjs(Date.now()).utc().format("DD-MMM-YYYY HH:mm:ss");
// Sep 2026 - shared accounting-style currency formatter for every $ figure across Platform
// Maintenance's Analytics/Revenue Analytics/Executive Report tools - added after a live review
// found these all hand-rolled their own `$${x.toFixed(2)}` string, which drops thousands
// separators (e.g. "$54826.08" instead of "$54,826.08") and has no real convention for a negative
// amount. currencySign: 'accounting' renders a negative as "($224.00)" rather than "-$224.00",
// matching how a real financial report would show it - relevant here since a few of these tiles
// are period-over-period deltas that can genuinely go negative.
export const formatCurrency = (amount?: number | null) =>
    typeof amount === 'number' && !Number.isNaN(amount)
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', currencySign: 'accounting' }).format(amount)
        : '—';
export const parseDataUrl = (dataUrl: string, mimeType: string = 'application/pdf') => {
    return dataUrl?.replace(`data:${mimeType};base64,`, '');
}

export const hasAbornmalResults = (order?: OrderItem): boolean =>
    ['yes', 'true', 'y'].some(x => x === order?.abnormalResult?.toLowerCase())

// Oct 2026 - mirrors the server's OrderReportAccess rule (the server is what actually withholds a draft report; this
// only decides whether to offer the report icon). The lab generates every report as soon as results are exported,
// including those it holds for a lab manager to review, so "has a report" is not "has a releasable report". A report
// counts when it is posted, OR the order itself has been released. The posted flag alone is not enough: many delivered,
// Completed orders in production never had it set.
const RELEASED_SAMPLE_STATUSES = ['results ready', 'results posted'];
const RELEASED_ORDER_STATUSES = ['completed', 'results posted'];
export const isOrderReleased = (order?: { sampleStatus?: string | null; orderStatus?: string | null }): boolean =>
  RELEASED_SAMPLE_STATUSES.includes((order?.sampleStatus ?? '').trim().toLowerCase())
  && RELEASED_ORDER_STATUSES.includes((order?.orderStatus ?? '').trim().toLowerCase());

export const hasReports = (order?: OrderItem): boolean => {
  const released = isOrderReleased(order);
  const slots: [string | null | undefined, boolean | null | undefined][] = [
    [order?.pdfReport1TestName, order?.pdfReport1Posted],
    [order?.pdfReport2TestName, order?.pdfReport2Posted],
    [order?.pdfReport3TestName, order?.pdfReport3Posted],
    [order?.pdfReport4TestName, order?.pdfReport4Posted],
    [order?.pdfReport5TestName, order?.pdfReport5Posted],
  ];
  return slots.some(([testName, posted]) => testName !== null && testName !== undefined && (posted === true || released));
};

// Oct 2026 - the order has a generated report but none the provider can see: drafts only. The grid shows a "Draft" chip
// instead of the report icon for the people who may still open them (Portal Admin), so a draft is never mistaken for
// a released report.
export const hasOnlyDraftReports = (order?: OrderItem): boolean => {
  const hasAnySlot = [order?.pdfReport1TestName, order?.pdfReport2TestName, order?.pdfReport3TestName, order?.pdfReport4TestName, order?.pdfReport5TestName]
    .some(testName => testName !== null && testName !== undefined);
  return hasAnySlot && !hasReports(order);
};

export const hasLabMessage = (order?: OrderItem) : boolean => order?.needsContact === NEEDS_CONTACT_PROVIDER;
export const parseOrderReports = (order?: OrderItem | OrderItemReports) : OrderReport[] => {
    const reports: OrderReport[] = [];

    if(order?.pdfReport1TestName){
        reports.push({
            reportFile: order?.pdfReport1,
            reportDatetime: order?.pdfReport1Datetime,
            reportPosted: order?.pdfReport1Posted,
            reportDraft: (order as OrderItemReports | undefined)?.pdfReport1Draft,
            reportTestName: order?.pdfReport1TestName,
            reportType: order?.pdfReport1Type,
        });
    }
    if(order?.pdfReport2TestName){
        reports.push({
            reportFile: order?.pdfReport2,
            reportDatetime: order?.pdfReport2Datetime,
            reportPosted: order?.pdfReport2Posted,
            reportDraft: (order as OrderItemReports | undefined)?.pdfReport2Draft,
            reportTestName: order?.pdfReport2TestName,
            reportType: order?.pdfReport2Type,
        });
    }
    if(order?.pdfReport3TestName){
        reports.push({
            reportFile: order?.pdfReport3,
            reportDatetime: order?.pdfReport3Datetime,
            reportPosted: order?.pdfReport3Posted,
            reportDraft: (order as OrderItemReports | undefined)?.pdfReport3Draft,
            reportTestName: order?.pdfReport3TestName,
            reportType: order?.pdfReport3Type,
        });
    }
    if(order?.pdfReport4TestName){
        reports.push({
            reportFile: order?.pdfReport4,
            reportDatetime: order?.pdfReport4Datetime,
            reportPosted: order?.pdfReport4Posted,
            reportDraft: (order as OrderItemReports | undefined)?.pdfReport4Draft,
            reportTestName: order?.pdfReport4TestName,
            reportType: order?.pdfReport4Type,
        });
    }
    if(order?.pdfReport5TestName){
        reports.push({
            reportFile: order?.pdfReport5,
            reportDatetime: order?.pdfReport5Datetime,
            reportPosted: order?.pdfReport5Posted,
            reportDraft: (order as OrderItemReports | undefined)?.pdfReport5Draft,
            reportTestName: order?.pdfReport5TestName,
            reportType: order?.pdfReport5Type,
        });
    }

    return reports;
}

export const mapGridStatus = (status?: boolean) => {
    if(status === undefined)
        return "*";

    if(status === true)
        return "true";

    if(status === false)
        return "false";
}

export const mapFromGridStatus = (status?: string): boolean | undefined => {
    if(status === '*')
        return undefined;

    if(status === 'true')
        return true;

    if(status === 'false')
        return false;

    return undefined;
}

export const getAvailableUserTypes = (currentUserType: string, roles: ISelectItem[]) => {
    if(!currentUserType)
        return [];

    console.log('filter roles type', currentUserType);
    console.log('filter roles', roles.filter(role => !USER_ROLES_AVAILABLE[currentUserType]?.includes(role.label)));
    return roles.filter(role => !USER_ROLES_AVAILABLE[currentUserType]?.includes(role.label));
}

// Oct 2026 - mirrors the server's CcFeeHelper.ShouldApplyFee exactly (SimplyTestServerSide/Utils/
// CcFeeHelper.cs) - the single source of truth for "does this card actually get charged the CC
// convenience fee." Before this existed, every confirm-charge screen (OrderForm's "Confirm
// Submission Charge" dialog, ShipToClinic, ShipToPatient) showed the fee line and fee-included
// total purely off paymentType === 'CC', even for a debit card on file - which the server has never
// actually charged the fee to, since cardFunding ("credit"/"debit"/"prepaid"/"unknown"/undefined)
// was in the API response (ResolvedPaymentSource) but no screen read it. Case-insensitive, same as
// the server - cardFunding is Stripe's own verbatim value, not one this codebase controls. Anything
// other than exactly "debit" keeps the fee, matching the server's fail-safe-toward-charging default.
export const shouldApplyCcFee = (paymentType?: string, cardFunding?: string) =>
    paymentType === 'CC' && (cardFunding ?? '').toLowerCase() !== 'debit';
